import * as React from 'react';

import {
  Col,
  ContentCard,
  ContentSection,
  PhotoPreviewDialog,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { v4 as uuidv4 } from 'uuid';

import { useLoadFiles } from '../../loads/hooks/useLoadFiles';
import { ScaleTicketPhotoGrid } from '../shared/ScaleTicketPhotoGrid';

import {
  $applyOptimisticUpdate,
  $markOptimisticUpdateError,
  $optimisticPhotoUpdates,
  $removeOptimisticUpdate,
} from '~/modules/trade/_state/files.state';
import {
  $loadPhotos,
  $loadPhotosWithOptimistic,
} from '~/modules/trade/_state/loadDetail.state';
import { usePhotoPreview } from '~/modules/trade/hooks/usePhotoPreview';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { GenericFile } from '~/modules/trade/types/file';
import { LoadRecord } from '~/modules/trade/types/load';
import { FilesTab } from '~/shared/components/files';
import { useFileDownload } from '~/shared/hooks/useFileDownload';

type OutboundTicketTabPhotosProps = {
  ticket: LoadRecord | null;
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET TAB PHOTOS ⚛️ ⚛️ ⚛️
//
export const OutboundTicketTabPhotos: React.FC<
  OutboundTicketTabPhotosProps
> = ({ ticket }) => {
  const t = useTranslations();

  // Refs
  const deleteFileRef = React.useRef<
    ((params: { fileId: string }) => Promise<void>) | null
  >(null);

  // Atoms
  const loadPhotosResult = useAtomValue($loadPhotosWithOptimistic);
  const baseLoadPhotosResult = useAtomValue($loadPhotos);
  const optimisticUpdates = useAtomValue($optimisticPhotoUpdates);
  const applyOptimisticUpdate = useSetAtom($applyOptimisticUpdate);
  const markOptimisticUpdateError = useSetAtom($markOptimisticUpdateError);
  const removeOptimisticUpdate = useSetAtom($removeOptimisticUpdate);

  // Data hooks
  const toast = useToast();
  const { downloadFile, isGeneratingDownloadUrl } = useFileDownload();
  const loadPhotoMutations = useLoadFiles();

  // Memoized values
  const photoFiles = React.useMemo<GenericFile[]>(() => {
    if (loadPhotosResult?.data) {
      return loadPhotosResult.data
        .filter((photo) => photo.mimeType.startsWith('image/'))
        .map((photo) => ({
          id: photo.id,
          fileName: photo.fileName || `temp-${uuidv4()}`,
          fileSize: photo.fileSize,
          mimeType: photo.mimeType,
          status: photo.status,
          downloadUrl: photo.url,
          entityType: 'Load',
          entityId: ticket?.id,
          metadata: photo.metadata || {},
          createdAt: photo.createdAt?.toString(),
          updatedAt: photo.updatedAt?.toString(),
          _optimistic: photo._optimistic,
        }));
    }
    return [];
  }, [loadPhotosResult?.data, ticket?.id]);

  const {
    previewOpen,
    currentPhotoIndex,
    previewPhotos,
    handlePhotoView,
    closePreview,
    navigatePreview,
  } = usePhotoPreview(photoFiles);

  const optimisticUpdatesFingerprint = React.useMemo(() => {
    const entries: string[] = [];
    optimisticUpdates.forEach((update, id) => {
      entries.push(`${id}:${update.type}:${update.status}`);
    });
    return entries.sort().join('|');
  }, [optimisticUpdates]);

  const renderDropZoneText = React.useMemo(
    () => ({
      primary: (
        <>
          {t.loadDetail.photos.dropZone.primaryClick}{' '}
          <span className="font-semibold text-primary">
            {t.loadDetail.photos.dropZone.primaryHere}
          </span>{' '}
          {t.loadDetail.photos.dropZone.primaryDrag}{' '}
        </>
      ),
      secondary: t.loadDetail.photos.dropZone.secondary,
    }),
    [t.loadDetail.photos.dropZone],
  );

  // Callbacks
  const handleAllUploadsComplete = React.useCallback(() => {
    baseLoadPhotosResult.refetch();
  }, [baseLoadPhotosResult]);

  const handleDeletePhoto = React.useCallback(
    async (photoId: string) => {
      const currentPhoto = photoFiles.find((p) => p.id === photoId);
      if (!currentPhoto) return;

      applyOptimisticUpdate({
        id: photoId,
        type: 'delete',
        previousData: currentPhoto as Record<string, unknown>,
      });

      try {
        await deleteFileRef.current?.({ fileId: photoId });
        toast.success(t.loadDetail.photos.photoDeleted);
      } catch (error) {
        console.error('Failed to delete photo:', error);
        markOptimisticUpdateError({
          id: photoId,
          error: t.loadDetail.photos.photoDeleteFailed,
        });
        toast.error(t.loadDetail.photos.photoDeleteFailed);
      }
    },
    [photoFiles, applyOptimisticUpdate, markOptimisticUpdateError, t, toast],
  );

  const handleRetryDelete = React.useCallback(
    async (photoId: string) => {
      const optimisticUpdate = optimisticUpdates.get(photoId);
      if (!optimisticUpdate || optimisticUpdate.status !== 'error') return;

      applyOptimisticUpdate({
        id: photoId,
        type: 'delete',
        previousData: optimisticUpdate.previousData,
      });

      try {
        await deleteFileRef.current?.({ fileId: photoId });
        toast.success(t.loadDetail.photos.photoDeleted);
      } catch (_error) {
        markOptimisticUpdateError({
          id: photoId,
          error: t.loadDetail.photos.photoDeleteFailed,
        });
        toast.error(t.loadDetail.photos.photoDeleteFailed);
      }
    },
    [
      optimisticUpdates,
      applyOptimisticUpdate,
      markOptimisticUpdateError,
      toast,
      t,
    ],
  );

  const handleDismissOptimisticError = React.useCallback(
    (photoId: string) => {
      removeOptimisticUpdate(photoId);
    },
    [removeOptimisticUpdate],
  );

  // Effects
  /** Sync deleteFile ref with latest mutation */
  React.useEffect(() => {
    deleteFileRef.current = loadPhotoMutations.deleteFile;
  }, [loadPhotoMutations.deleteFile]);

  /** Clean up optimistic updates once server confirms deletion */
  React.useEffect(() => {
    if (!baseLoadPhotosResult.data) return;

    const serverPhotoIds = new Set(baseLoadPhotosResult.data.map((p) => p.id));

    optimisticUpdates.forEach((update, photoId) => {
      if (update.type === 'delete' && !serverPhotoIds.has(photoId)) {
        removeOptimisticUpdate(photoId);
      }
    });
  }, [
    baseLoadPhotosResult.data,
    optimisticUpdatesFingerprint,
    removeOptimisticUpdate,
  ]);

  return (
    <Col gap5 className="@OutboundTicketTabPhotos relative">
      <ContentCard className="overflow-visible pt-0">
        <ContentCard className="border-none bg-none px-0">
          <ContentSection
            title={t.loadDetail.photos.title}
            titleClassName="font-bold text-neutral"
          >
            <FilesTab
              entityId={ticket?.id}
              files={photoFiles}
              mutations={loadPhotoMutations}
              onAllUploadsComplete={handleAllUploadsComplete}
              mimeType="image/*"
              renderItems={(files, _defaultRenderer, uploadErrorActions) => (
                <ScaleTicketPhotoGrid
                  files={files}
                  uploadErrorActions={uploadErrorActions}
                  downloadFile={downloadFile}
                  isGeneratingDownloadUrl={isGeneratingDownloadUrl}
                  onDeletePhoto={handleDeletePhoto}
                  onRetryDelete={handleRetryDelete}
                  onDismissOptimisticError={handleDismissOptimisticError}
                  onView={handlePhotoView}
                />
              )}
              dropZoneTheme="compact"
              dropZoneText={renderDropZoneText}
            />
          </ContentSection>
        </ContentCard>
      </ContentCard>
      <PhotoPreviewDialog
        albumName={t.loadDetail.photos.title}
        photos={previewPhotos}
        currentIndex={currentPhotoIndex}
        isOpen={previewOpen}
        onClose={closePreview}
        onNavigate={navigatePreview}
      />
    </Col>
  );
};
